import { Z } from "../../global/constants/zIndex";
import { useLayers } from "../../context/LayersContext";
import {
  Layer,
  mockLayers,
  defaultDimensionValue,
  hasSelectableDimension,
} from "../../global/constants/layers";
import { Button } from "../ui/buttons/button";
import { cn } from "../../lib/utils";
import { CompassRose } from "./CompassRose";

// Above this many options a column of buttons gets too tall for the map, so the
// selector collapses into a dropdown (e.g. 10 years). Direction dimensions use
// the compass rose instead, whatever their option count.
const MAX_BUTTON_OPTIONS = 6;

function formatOption(value: number, unit?: string) {
  return unit ? `${value}${unit}` : String(value);
}

interface DimensionSectionProps {
  layer: Layer;
  value: number;
  onChange: (value: number) => void;
}

function DimensionSection({ layer, value, onChange }: DimensionSectionProps) {
  const values = layer.temporal?.values ?? [];
  const unit = layer.temporal?.unit;
  const heading = layer.temporal?.label ?? layer.temporal?.dimension ?? "";

  return (
    <div className="flex w-full flex-col items-center gap-2">
      <h3 className="w-full text-center text-sm font-semibold text-primary">
        {heading}
      </h3>
      <span className="w-full text-center text-[11px] leading-tight text-muted-foreground">
        {layer.title}
      </span>

      {layer.temporal?.dimension === "direction" ? (
        <CompassRose
          values={values}
          value={value}
          onChange={onChange}
          ariaLabel={`${heading} — ${layer.title}`}
        />
      ) : values.length > MAX_BUTTON_OPTIONS ? (
        <select
          aria-label={`${heading} — ${layer.title}`}
          className="w-full rounded-md border border-input bg-white px-2 py-1 text-xs"
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
        >
          {values.map((option) => (
            <option key={option} value={option}>
              {formatOption(option, unit)}
            </option>
          ))}
        </select>
      ) : (
        <div className="flex w-full flex-col items-center gap-2">
          {values.map((option) => (
            <Button
              key={option}
              variant={option === value ? "default" : "outline"}
              onClick={() => onChange(option)}
              className={cn(
                "min-w-[60px] text-xs",
                option === value ? "font-semibold" : "font-normal"
              )}
            >
              {formatOption(option, unit)}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Selector for ImageMosaic dimensions (height, wind direction sector, year).
 * Renders one section per active layer that publishes a discrete value list.
 */
export function DimensionControl() {
  const { layers, layerElevationValues, setLayerElevation } = useLayers();

  const activeDimensionLayers = mockLayers.filter(
    (layer) => layers.includes(layer.title) && hasSelectableDimension(layer)
  );

  if (activeDimensionLayers.length === 0) return null;

  return (
    <div
      style={{
        position: "absolute",
        bottom: "20px",
        right: "20px",
        zIndex: Z.MAP_CONTROL,
        maxHeight: "60vh",
        overflowY: "auto",
      }}
      className="dimension-control-panel flex min-w-[130px] flex-col items-center gap-4 rounded-lg bg-white/95 p-4 shadow-md"
    >
      {activeDimensionLayers.map((layer) => (
        <DimensionSection
          key={layer.layerName}
          layer={layer}
          value={
            layerElevationValues[layer.layerName] ?? defaultDimensionValue(layer)
          }
          onChange={(value) => setLayerElevation(layer.layerName, value)}
        />
      ))}
    </div>
  );
}
